// One row in the <ActivityTimeline>. Render an avatar (initials in a
// colored circle, seeded by actor id for stability), the actor's name +
// localized action label + relative time, plus an optional from→to
// detail block in mono-style text.

import { cn } from "@/lib/utils";
import { formatRelativeVi } from "@/features/property/utils/relative-time";
import { labelForAction } from "@/features/dashboard/activity-action-labels";
import { formatActivityDetail } from "@/features/activity/format-activity-detail";
import type { ActivityLogResponse } from "@/features/activity/api";

interface ActivityRowProps {
  item: ActivityLogResponse;
  /** True when this row is the last in the timeline. Hides the joining line. */
  isLast?: boolean | undefined;
}

// Deterministic color palette — seed by user id so the same broker
// always shows the same avatar color across sessions.
const AVATAR_COLORS = [
  "bg-blue-100 text-blue-700",
  "bg-emerald-100 text-emerald-700",
  "bg-amber-100 text-amber-700",
  "bg-rose-100 text-rose-700",
  "bg-violet-100 text-violet-700",
  "bg-cyan-100 text-cyan-700",
  "bg-fuchsia-100 text-fuchsia-700",
] as const;

function colorForUser(userId: number): string {
  // The non-null assertion is safe — modulo positive index into a
  // non-empty const tuple always returns a defined slot.
  const idx = Math.abs(userId) % AVATAR_COLORS.length;
  return AVATAR_COLORS[idx]!;
}

function initialsFor(fullName: string): string {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 0 || parts[0] === undefined) return "?";
  if (parts.length === 1) return (parts[0]?.[0] ?? "?").toUpperCase();
  const first = parts[0][0] ?? "";
  const last = parts[parts.length - 1]?.[0] ?? "";
  return `${first}${last}`.toUpperCase();
}

export function ActivityRow({ item, isLast = false }: ActivityRowProps) {
  // Codegen marks every wire field as optional; the backend always sends
  // them, so we fall back to safe defaults rather than threading
  // conditionals through the render tree.
  const action = item.action ?? "CREATED";
  const actorFullName = item.actorFullName ?? "Hệ thống";
  const actorUserId = item.actorUserId ?? 0;
  const createdAt = item.createdAt ?? "";
  const detail = item.detail ?? null;
  const parsedDetail = formatActivityDetail(action, detail);
  return (
    <li className="relative flex gap-3 pb-4 last:pb-0">
      {/* Vertical connector line — hidden on the last row */}
      {!isLast ? (
        <span
          aria-hidden="true"
          className="absolute left-4 top-9 bottom-0 w-px bg-border"
        />
      ) : null}
      <div
        aria-hidden="true"
        className={cn(
          "relative z-10 flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
          colorForUser(actorUserId),
        )}
      >
        {initialsFor(actorFullName)}
      </div>
      <div className="min-w-0 flex-1 pt-0.5">
        <p className="text-sm leading-snug">
          <span className="font-medium">{actorFullName}</span>{" "}
          <span className="text-muted-foreground">
            {labelForAction(action)}
          </span>
        </p>
        {parsedDetail.summary !== null ? (
          <p
            className={cn(
              "mt-1 inline-block rounded-md bg-muted px-2 py-0.5 font-mono text-xs",
              parsedDetail.isTransition
                ? "text-foreground"
                : "text-muted-foreground",
            )}
          >
            {parsedDetail.summary}
          </p>
        ) : null}
        <p className="mt-1 text-xs text-muted-foreground">
          {formatRelativeVi(createdAt)}
        </p>
      </div>
    </li>
  );
}
